/**
 * Reloj del HUD de la sala de operaciones.
 *
 * Muestra la hora local del usuario en el elemento `#clk` (HUD de la escena)
 * y opcionalmente en `#fclk` (otra ubicación, como un footer). Se actualiza
 * cada segundo.
 *
 * IMPORTANTE: usa la hora LOCAL del usuario, no la de Madrid. Si el escuadrón
 * decide en algún momento mostrar zona horaria fija, sería un parámetro nuevo.
 *
 * Uso:
 *   import { startClock } from './clock';
 *   startClock();              // arranca con selectores por defecto
 *   startClock({ ids: ['clk', 'fclk', 'otro'] });   // selectores custom
 */

export interface ClockOptions {
  /** IDs de los elementos donde se va a renderizar la hora. */
  ids?: string[];
  /** Locale para formato. Por defecto 'es-ES'. */
  locale?: string;
}

let intervalHandle: number | null = null;

/** Renderiza la hora actual en todos los elementos con los IDs indicados. */
function renderTime(ids: string[], locale: string): void {
  const t = new Date().toLocaleTimeString(locale, {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  for (const id of ids) {
    const el = document.getElementById(id);
    if (el) el.textContent = t;
  }
}

/**
 * Arranca el reloj. Idempotente: llamar varias veces no duplica el intervalo,
 * sólo aplica los nuevos parámetros.
 */
export function startClock(options: ClockOptions = {}): void {
  const ids = options.ids ?? ['clk', 'fclk'];
  const locale = options.locale ?? 'es-ES';

  // Si ya había un reloj corriendo, lo paramos antes (evita duplicados).
  stopClock();

  // Pintar inmediatamente (sin esperar al primer segundo).
  renderTime(ids, locale);

  // setInterval en navegador devuelve number; en Node devuelve Timeout.
  // Como esto sólo corre en navegador, casteamos.
  intervalHandle = window.setInterval(() => renderTime(ids, locale), 1000);
}

/** Detiene el reloj si estaba corriendo. */
export function stopClock(): void {
  if (intervalHandle !== null) {
    window.clearInterval(intervalHandle);
    intervalHandle = null;
  }
}
